import React, { useState, useEffect } from "react";
import { InboxIcon } from "lucide-react";
import AppLayout from "../Layout/AppLayout";

const statusConfig = {
  pending: {
    label: "PENDING",
    dotColor: "bg-amber-500",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/20",
    textColor: "text-amber-400",
  },
  matched: {
    label: "MATCHED",
    dotColor: "bg-blue-500",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/20",
    textColor: "text-blue-400",
  },
  accepted: {
    label: "ACCEPTED",
    dotColor: "bg-[hsl(173,58%,45%)]",
    bgColor: "bg-[rgba(45,160,140,0.1)]",
    borderColor: "border-[rgba(45,160,140,0.3)]",
    textColor: "text-[hsl(173,58%,45%)]",
  },
  completed: {
    label: "COMPLETED",
    dotColor: "bg-green-500",
    bgColor: "bg-green-500/10",
    borderColor: "border-green-500/20",
    textColor: "text-green-400",
  },
  declined: {
    label: "DECLINED",
    dotColor: "bg-red-500",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/20",
    textColor: "text-red-400",
  },
};

export default function PartnerDashboard({
  leads = [],
  loading = false,
  persona = "PARTNER",
  userName = "",
  userInitials = "",
  planProgress = { completed: 0, total: 0, percentage: 0 },
  actionPlanBadge = 0,
  isPartner = true,
}) {
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    if (typeof window !== "undefined" && window.ahoy) {
      window.ahoy.track("succeedo_partner_dashboard_viewed");
    }
  }, []);

  const filteredLeads =
    activeFilter === "all"
      ? leads
      : leads.filter((lead) => lead.status === activeFilter);

  const stats = {
    pending: leads.filter((l) => l.status === "pending").length,
    matched: leads.filter((l) => l.status === "matched").length,
    accepted: leads.filter((l) => l.status === "accepted").length,
    completed: leads.filter((l) => l.status === "completed").length,
  };

  return (
    <AppLayout
      breadcrumb="PARTNER DASHBOARD"
      persona={persona}
      userName={userName}
      userInitials={userInitials}
      isPartner={isPartner}
      planProgress={planProgress}
      actionPlanBadge={actionPlanBadge}
    >
      <div className="p-6">
        {/* Partner Identity Strip */}
        <div className="bg-card/50 border border-border rounded-[16px] p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[hsl(var(--succeedo-orange))]/10 text-[hsl(var(--succeedo-orange))] flex items-center justify-center font-mono text-[14px] font-medium">
              {userInitials}
            </div>
            <div>
              <div className="text-[20px] font-light text-foreground">
                {userName}
              </div>
              <div className="text-[13px] text-muted-foreground">
                Partner
              </div>
            </div>
          </div>
          <div className="bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/20 text-[hsl(var(--succeedo-orange))] rounded-full px-3 py-1 font-mono text-[11px] uppercase tracking-wide">
            CBV
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {Object.entries(stats).map(([key, value]) => {
            const config = statusConfig[key];
            return (
              <div
                key={key}
                className="bg-card border border-border rounded-[12px] p-4"
              >
                <div className="font-mono text-[10px] uppercase text-muted-foreground/50 tracking-wide mb-2 flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${config.dotColor}`} />
                  {config.label}
                </div>
                <div className="text-[28px] font-light text-foreground">
                  {value}
                </div>
              </div>
            );
          })}
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 mb-4">
          {["all", "pending", "matched", "accepted", "completed"].map(
            (filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-wide transition-colors ${
                  activeFilter === filter
                    ? "bg-foreground text-background"
                    : "bg-transparent border border-border text-muted-foreground hover:bg-muted"
                }`}
              >
                {filter}
              </button>
            )
          )}
        </div>

        {/* Section Label */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2 h-2 rounded-full bg-[hsl(var(--succeedo-orange))] shadow-[0_0_8px_hsl(var(--succeedo-orange))]" />
          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/40">
            RECENT LEADS
          </span>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="text-[14px] text-muted-foreground">
              Loading leads...
            </div>
          </div>
        )}

        {/* Leads List or Empty State */}
        {!loading && leads.length > 0 && filteredLeads.length > 0 ? (
          <div className="space-y-3">
            {filteredLeads.map((lead) => {
              const config = statusConfig[lead.status] || statusConfig.pending;
              return (
                <div
                  key={lead.id}
                  onClick={() => {
                    window.location.href = `/succeedo/partner/leads/${lead.id}`;
                  }}
                  className="block bg-card border border-border rounded-[12px] p-5 hover:border-[hsl(var(--succeedo-orange))]/30 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-4">
                    {/* Owner Avatar */}
                    <div className="w-10 h-10 rounded-full bg-muted text-foreground flex items-center justify-center font-mono text-[12px] font-medium shrink-0">
                      {lead.owner_initials || lead.ownerInitials}
                    </div>

                    {/* Center Column */}
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-medium text-foreground mb-0.5">
                        {lead.owner_name || lead.ownerName}
                      </div>
                      <div className="text-[13px] text-muted-foreground mb-0.5">
                        {lead.industry}
                      </div>
                      <div className="font-mono text-[11px] text-muted-foreground/50">
                        {lead.province}
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="text-right shrink-0">
                      <div
                        className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wide border ${config.bgColor} ${config.borderColor} ${config.textColor} mb-2`}
                      >
                        {config.label}
                      </div>
                      <div className="text-[12px] text-muted-foreground/40">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : lead.timestamp}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          !loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <InboxIcon
                className="w-12 h-12 text-muted-foreground/20 mb-4"
                strokeWidth={1.5}
              />
              <div className="text-[20px] font-light text-foreground mb-2">
                No leads yet
              </div>
              <div className="text-[14px] text-muted-foreground mb-6 max-w-md text-center">
                When business owners request an introduction, they'll appear
                here.
              </div>
              <a
                href="/succeedo/partner/profile"
                className="border border-border text-muted-foreground rounded-full px-5 py-2 text-[14px] hover:bg-muted transition-colors"
              >
                View My Profile &rarr;
              </a>
            </div>
          )
        )}
      </div>
    </AppLayout>
  );
}
